import Cookies from "js-cookie";
import { FEATURES } from "settings";

export type Features = typeof FEATURES;

export type FeatureName = keyof Features;

export type Variations<Feature extends FeatureName> =
  Features[Feature]["variations"];

export type VariationKey<Feature extends FeatureName> =
  keyof Variations<Feature>;

export type VariationName<Feature extends FeatureName> =
  Variations<Feature>[VariationKey<Feature>];

const CACHE: { [Key in FeatureName]?: string } = {};

const INACTIVE_EXPERIMENT = "-1";

export const FEATURE_SEARCH_PARAM_PREFIX = "f_";

export function getActiveExperiment() {
  Object.keys(FEATURES).forEach((key) => {
    const feature = key as FeatureName;
    const featureConfig = FEATURES[feature];

    // we add everything into cache, not necessarily experiments.
    if (!CACHE[feature]) {
      const cookieVariation = Cookies.get(featureConfig.key);
      if (cookieVariation && cookieVariation !== INACTIVE_EXPERIMENT) {
        CACHE[feature] = cookieVariation;
      }
    }
  });

  const activeExperiment = (Object.keys(CACHE) as FeatureName[]).find((key) => {
    const experiment = FEATURES[key];

    return (
      "isExperiment" in experiment &&
      experiment.isExperiment &&
      CACHE[key] !== INACTIVE_EXPERIMENT
    );
  });

  if (activeExperiment && CACHE[activeExperiment]) {
    const currentKey = CACHE[
      activeExperiment
    ] as keyof typeof experiment.variations;
    const experiment = FEATURES[activeExperiment];
    const variantName = experiment.variations[currentKey];
    return [experiment.key, variantName];
  }
}

export function getAvailableFeatures() {
  Object.keys(FEATURES).forEach((key) => {
    const feature = key as FeatureName;
    if (typeof CACHE[feature] === "undefined") {
      const cookieVariation = Cookies.get(FEATURES[feature].key);

      if (typeof cookieVariation !== "undefined") {
        CACHE[feature] = cookieVariation;
      }
    }
  });

  return Object.entries(CACHE).reduce<Record<string, string>>((acc, val) => {
    const key = FEATURES[val[0] as FeatureName].key;
    acc[key] = val[1];
    return acc;
  }, {});
}

export function getFeatureKey(feature: FeatureName): string {
  return FEATURES[feature].key;
}

export function getFeatureVariation(feature: FeatureName) {
  if (typeof CACHE[feature] === "undefined") {
    const cookieVariation = Cookies.get(FEATURES[feature].key);

    if (typeof cookieVariation === "undefined") {
      Cookies.set(FEATURES[feature].key, FEATURES[feature].defaultVariation);

      CACHE[feature] = FEATURES[feature].defaultVariation;
    } else {
      CACHE[feature] = cookieVariation;
    }
  }

  return FEATURES[feature].variations[
    CACHE[feature] as VariationKey<FeatureName>
  ];
}

export function setFeatureVariationsFromSearchParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const featureParams = Array.from(urlParams.keys()).filter((key) => {
    return key.startsWith(FEATURE_SEARCH_PARAM_PREFIX);
  });
  featureParams.forEach((featureParam) => {
    const obfuscatedFeatureKey = featureParam.replace(
      FEATURE_SEARCH_PARAM_PREFIX,
      "",
    );

    const featureKey = atob(decodeURIComponent(obfuscatedFeatureKey));
    const featureName = (Object.keys(FEATURES) as FeatureName[]).find(
      (currKey) => FEATURES[currKey].key === featureKey,
    );
    const currentFeature = FEATURES[featureName as FeatureName];
    const featureVariation = urlParams.get(featureParam);

    if (featureName && currentFeature && featureVariation) {
      const featureVariationKey = Object.keys(currentFeature.variations).find(
        (key) =>
          currentFeature.variations[key as VariationKey<FeatureName>] ===
          atob(decodeURIComponent(featureVariation)),
      );

      if (featureVariationKey) {
        Cookies.set(currentFeature.key, featureVariationKey);
        CACHE[featureName] = featureVariationKey;
      }
    }
  });
}

export function getObfuscatedFeatureSearchParam(feature: FeatureName) {
  const key = getFeatureKey(feature);
  const variation = getFeatureVariation(feature);

  const obfuscateFeatureKey = encodeURIComponent(btoa(key));
  const obfuscateFeatureVariation = encodeURIComponent(btoa(variation));
  return `${FEATURE_SEARCH_PARAM_PREFIX}${obfuscateFeatureKey}=${obfuscateFeatureVariation}`;
}

export function setFeatureVariation<Feature extends FeatureName>(
  feature: Feature,
  variationName: VariationName<Feature>,
) {
  for (const variation of Object.entries(FEATURES[feature].variations)) {
    if (variation[1] === variationName) {
      Cookies.set(FEATURES[feature].key, variation[0]);
      CACHE[feature] = variation[0];
      return;
    }
  }
}
